(ns story-builder.story-editor.controller.sub
  (:require
   [clojure.string :as str]
   [re-frame.core :as rf]
   [story-builder.font.sub :as-alias font.sub]
   [story-builder.story-editor.breakpoint.sub :as-alias breakpoint.sub]
   [story-builder.story-editor.breakpoint.util :as breakpoint.util]
   [story-builder.story-editor.cta-sidebar.sub :as-alias cta-sidebar.sub]
   [story-builder.story-editor.controller.util :as controller.util]
   [story-builder.story-editor.interactive.util :as interactive.util]
   [story-builder.story-editor.layer.util :as layer.util]
   [story-builder.story-editor.rich-text.content :as rich-text.content]
   [story-builder.story-editor.rich-text.editor :as-alias rich-text-editor]
   [story-builder.story-editor.ruler.sub :as ruler.sub]
   [story-builder.story-editor.scene-control.sub :as-alias scene-control.sub]
   [story-builder.sub.scene :as-alias scene.sub]
   [story-builder.util.core :as util]
   [story-builder.util.math :as util.math]))

(rf/reg-sub
 ::controller
 (fn [db _]
   (get db :controller)))

(rf/reg-sub
 ::hover
 :<- [::controller]
 (fn [db _]
   (get db :hover)))

(rf/reg-sub
 ::selection
 :<- [::controller]
 (fn [db _]
   (get db :selection #{})))

(rf/reg-sub
 ::scene-has-selection?
 :<- [:scenes/current-id]
 :<- [::selection]
 (fn [[current-scene-id selection] [_ scene-id]]
   (and (= scene-id current-scene-id)
        (boolean (seq selection)))))

(rf/reg-sub
 ::scene-has-selection-or-hover?
 :<- [:scenes/current-id]
 :<- [::selection]
 :<- [::hover]
 (fn [[current-scene-id selection hover] [_ scene-id]]
   (and (= scene-id current-scene-id)
        (or (boolean (seq selection))
            (boolean (get hover :layer-id))))))

(rf/reg-sub
 ::show-group-selection-sidebar?
 :<- [:wrapper/mouse-selection?]
 :<- [::selection]
 (fn [[mouse-selection? selection] _]
   (and (> (count selection) 1)
        (not mouse-selection?))))

(rf/reg-sub
 ::selected?
 :<- [::selection]
 (fn [selection [_ layer-id]]
   (and (= (count selection) 1)
        (= layer-id (first selection)))))

(rf/reg-sub
 ::hovered-on-scene?
 :<- [::hover]
 (fn [db [_ layer-id]]
   (and (= layer-id (get db :layer-id))
        (get db :layer?))))

(rf/reg-sub
 ::hovered-on-sidebar?
 :<- [::hover]
 (fn [db [_ layer-id]]
   (and (= layer-id (get db :layer-id))
        (not (get db :layer?)))))

(rf/reg-sub
 ::selected-layers
 :<- [::controller]
 :<- [:story/current-scene]
 (fn [[db current-scene] _]
   (let [layer-ids (get db :selection #{})]
     (filterv (fn [layer] (contains? layer-ids (:id layer))) (:layers current-scene)))))

(rf/reg-sub
 ::selected-layer
 :<- [::selected-layers]
 (fn [layers _]
   (and (= (count layers) 1)
        (first layers))))

(rf/reg-sub
 ::selected-layer-id
 :<- [::selected-layer]
 (fn [layer _]
   (:id layer)))

(rf/reg-sub
 ::selected-layer-keys
 :<- [::selected-layer]
 (fn [layer [_ ks]]
   (select-keys layer ks)))

(rf/reg-sub
 ::selected-layer-type
 :<- [::selected-layer]
 (fn [layer _]
   (:type layer)))

(rf/reg-sub
 ::selected-layer-link
 :<- [::selected-layer]
 (fn [layer _]
   (:link layer)))

(rf/reg-sub
 ::selected-layer-thumbnail
 :<- [::selected-layer]
 (fn [layer _]
   (get layer (case (:type layer) "video" :poster :src))))

(rf/reg-sub
 ::selected-layer-custom-video-poster
 :<- [::selected-layer]
 (fn [layer _]
   (get layer :custom-poster)))

(rf/reg-sub
 ::selected-layer-filters
 :<- [::selected-layer]
 (fn [layer _]
   (:filters layer)))

(rf/reg-sub
 ::selected-layer-file-name
 :<- [::selected-layer]
 (fn [layer _]
   (:file-name layer)))

(rf/reg-sub
 ::selected-layer-animations
 :<- [::selected-layer]
 (fn [layer _]
   (:animations layer)))

(rf/reg-sub
 ::selected-layer-animated?
 :<- [::selected-layer]
 (fn [layer _]
   (or (:animation layer)
       (:animations layer))))

(rf/reg-sub
 ::selected-layer-auto-width?
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 :<- [::scene.sub/layer-constraints]
 (fn [[layer active-breakpoints layer-constraints] _]
   (controller.util/layer-auto-width? layer active-breakpoints layer-constraints)))

(rf/reg-sub
 ::selected-layer-auto-height?
 :<- [:story/story-type]
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 :<- [::scene.sub/layer-constraints]
 (fn [[story-type layer active-breakpoints layer-constraints] _]
   (controller.util/layer-auto-height? layer active-breakpoints layer-constraints story-type)))

(rf/reg-sub
 ::selected-layer-style-prop
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] [_ prop]]
   (get (breakpoint.util/get-props layer active-breakpoints :style) prop)))

(rf/reg-sub
 ::mouse-selection-disabled?
 :<- [::ruler.sub/dragging-guide?]
 :<- [:scenes/current-layout]
 :<- [:scenes/mode]
 :<- [::scene-control.sub/interaction-mode]
 :<- [::move-mode]
 :<- [:story-builder.story-editor.scene-control.sub/selected-layer-is-background?]
 (fn [[dragging-guide?
       current-layout
       scenes-mode
       interaction-mode
       selected-move-mode
       selected-is-background?] _]
   (or (not= scenes-mode :scene)
       (not= interaction-mode :interaction-mode/default)
       (not= current-layout "freestyle")
       dragging-guide?
       (and (= selected-move-mode :contained)
            selected-is-background?))))

(rf/reg-sub
 ::mode
 :<- [::controller]
 (fn [db _]
   (:mode db)))

(rf/reg-sub
 ::moveable-selection?
 :<- [::controller]
 :<- [:story/current-scene]
 (fn [[controller current-scene] _]
   (let [layers          (:layers current-scene)
         selected-layers (filterv #(contains? (:selection controller) (:id %)) layers)
         moveable?       (and (seq selected-layers)
                              (every? (fn [{:keys [type]}]  ; FIXME: replace with centrally defined predicate abstraction, e.g. dynamic-layers
                                        (contains? (into #{"captions"
                                                           "title"
                                                           "rich-text"
                                                           "subheading"
                                                           "text"
                                                           "author"
                                                           "date"
                                                           "product-tag"
                                                           "img"
                                                           "video"
                                                           "shape"
                                                           "slideshow"
                                                           "reveal"
                                                           "scratch"
                                                           "embed"}
                                                         interactive.util/interactive-layer-types) type))
                                      selected-layers))]
     moveable?)))

(rf/reg-sub
 ::suppress-group-pointer-events?
 :<- [::controller]
 (fn [db _]
   (:suppress-group-pointer-events? db)))

(rf/reg-sub
 ::moving?
 :<- [::controller]
 (fn [db _]
   (= (:mode db) :move)))

(rf/reg-sub
 ::rotating?
 :<- [::controller]
 (fn [db _]
   (= (:mode db) :rotate)))

(rf/reg-sub
 ::resizing?
 :<- [::controller]
 (fn [db _]
   (= (:mode db) :resize)))

(rf/reg-sub
 ::display-order?
 :<- [::controller]
 (fn [db _]
   (:display-order? db)))

(rf/reg-sub
 ::editing?
 :<- [::controller]
 (fn [db [_ layer-id]]
   (when-let [editing-layer-id (:editing db)]
     (= editing-layer-id layer-id))))

(rf/reg-sub
 ::keep-ar?
 :<- [::controller]
 :<- [::scene-control.sub/selected-layer-is-stretched?]
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 :<- [::scene.sub/layer-constraints]
 (fn [[controller stretched? layer active-breakpoints layer-constraints] _]
   (if stretched?
     false
     (or (and (= (breakpoint.util/get-path layer active-breakpoints [:style :height]) "auto")
              (not= (breakpoint.util/get-path layer-constraints active-breakpoints [(:id layer) :keep-ar]) :width))
         (breakpoint.util/get-path layer-constraints active-breakpoints [(:id layer) :keep-ar])
         (get-in controller [:keep-ar? :group])))))

(rf/reg-sub
 ::move-mode
 :<- [::controller]
 (fn [db _]
   (:move-mode db)))

(rf/reg-sub
 ::layer
 :<- [::controller]
 (fn [db [_ layer-id]]
   (get-in db [:layers layer-id])))

(rf/reg-sub
 ::hover-bounding-rect
 :<- [::controller]
 (fn [db [_ layer-id]]
   (let [hover (:hover db)]
     (when (= (:layer-id hover) layer-id)
       (get hover :bounding-rect)))))

(rf/reg-sub
 ::bounding-rect-when-selected-or-hovered
 :<- [::controller]
 (fn [db [_ layer-id]]
   (let [{:keys [selection hover]} db]
     (when (or (contains? selection layer-id)
               (= (:layer-id hover) layer-id))
       (get db :bounding-rect)))))

(rf/reg-sub
 ::bounding-rect
 :<- [::controller]
 (fn [db _]
   (get db :bounding-rect)))

(rf/reg-sub
 ::rotation
 :<- [::bounding-rect]
 (fn [bounding-rect _]
   (get bounding-rect :rotation)))

(rf/reg-sub
 ::%-bounding-rect
 :<- [::bounding-rect]
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 :<- [::mode]
 :<- [:utils/scene-rect]
 :<- [:ui/outer-scene-rect]
 :<- [:utils/reference-em]
 :<- [::%-width?]
 :<- [::%-height?]
 (fn [[bounding-rect selected-layer active-breakpoints controller-mode scene-rect outer-scene-rect reference-em %-width? %-height?] _]
   (let [style             (breakpoint.util/get-props selected-layer active-breakpoints :style)
         scene-rect        (if (or %-width? %-height?)
                             outer-scene-rect
                             scene-rect)]
     (cond-> (-> (util.math/corners->bounding-rect (:corners bounding-rect))
                 (controller.util/scene-percentage-rect scene-rect reference-em %-width? %-height?))
       (and selected-layer (not controller-mode))
       (assoc :max-width
              (let [max-width (:max-width style)]
                (if (string? max-width)
                  (-> max-width
                      (str/replace #"%" "")
                      (str/replace #"em" "")
                      (js/parseFloat 10))
                  max-width))
              :width
              (let [width (:width style)]
                (if (contains? #{"max-content" "auto"} width)
                  (if %-width?
                    (* (/ (:width bounding-rect) (:width scene-rect)) 100)
                    (/ (:width bounding-rect) reference-em))
                  (if (string? width)
                    (-> width
                        (str/replace #"%" "")
                        (str/replace #"em" "")
                        (js/parseFloat 10))
                    width)))
              :height
              (let [height (:height style)]
                (if (= height "auto")
                  (if %-height?
                    (* (/ (:height bounding-rect) (:height scene-rect)) 100)
                    (/ (:height bounding-rect) reference-em))
                  (if (and (string? height) (not= height "auto"))
                    (-> height
                        (str/replace #"%" "")
                        (str/replace #"em" "")
                        (js/parseFloat 10))
                    height)))
              :left
              (let [left (:left style)]
                (if (string? left)
                  (-> left
                      (str/replace #"%" "")
                      (js/parseFloat 10))
                  left))
              :top
              (let [top (:top style)]
                (if (string? top)
                  (-> top
                      (str/replace #"%" "")
                      (js/parseFloat 10))
                  top)))))))

(rf/reg-sub
 ::%-width
 :<- [::%-bounding-rect]
 (fn [db _]
   (:width db)))

(rf/reg-sub
 ::max-width
 :<- [::%-bounding-rect]
 (fn [db _]
   (:max-width db)))

(rf/reg-sub
 ::active-guides
 :<- [::controller]
 (fn [db _]
   (get db :active-guides)))

(rf/reg-sub
 ::selected-replaceable-media-layer
 :<- [:scenes/layers]
 :<- [::selection]
 (fn [[layers selection] _]
   (when (= (count selection) 1)
     (let [layer (util/find-first #(= (:id %) (first selection)) layers)]
       (when (contains? #{"shape" "img" "video" "audio"} (:type layer))
         layer)))))

(rf/reg-sub
 ::replaceable-media-layer-selected?
 :<- [::selected-replaceable-media-layer]
 (fn [layer _]
   (some? layer)))

(rf/reg-sub
 ::selected-media
 :<- [::selected-layer]
 (fn [selected-layer _]
   (when-let [media-id (:media-id selected-layer)]
     {:id media-id})))

(rf/reg-sub
 ::active-layers
 :<- [:scenes/current-layers]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layers active-breakpoints] _]
   (filterv #(not (breakpoint.util/get-prop % active-breakpoints :disabled?)) layers)))

(rf/reg-sub
 ::alignment
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (controller.util/layer-alignment-vector layer active-breakpoints)))

(rf/reg-sub
 ::align-relative-to
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (or (breakpoint.util/get-prop layer active-breakpoints :align-relative-to) :responsive)))

(rf/reg-sub
 ::object-fit
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (or (breakpoint.util/get-prop layer active-breakpoints :object-fit) :cover)))

(rf/reg-sub
 ::selected-layer-actual-width
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (breakpoint.util/get-path layer active-breakpoints [:style :width])))

(rf/reg-sub
 ::selected-layer-actual-height
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (breakpoint.util/get-path layer active-breakpoints [:style :height])))

(rf/reg-sub
 ::%-width?
 :<- [::align-relative-to]
 :<- [::scene-control.sub/selected-layer-is-stretched-horizontally?]
 :<- [::selected-layer-actual-width]
 (fn [[align-relative-to stretched-horizontally? actual-width] _]
   (or (= align-relative-to :full)
       stretched-horizontally?
       (and (string? actual-width)
            (not (str/includes? actual-width "em"))
            (not (contains? #{"auto" "max-content"} actual-width))))))

(rf/reg-sub
 ::%-height?
 :<- [::align-relative-to]
 :<- [::scene-control.sub/selected-layer-is-stretched-vertically?]
 :<- [::selected-layer-actual-height]
 (fn [[align-relative-to stretched-vertically? actual-height] _]
   (or (= align-relative-to :full)
       stretched-vertically?
       (and (string? actual-height)
            (not (str/includes? actual-height "em"))
            (not= actual-height "auto")))))

(rf/reg-sub
 ::media-layer-corners
 (fn [db _]
   (get db :media-layer-corners)))

(rf/reg-sub
 ::z-position
 :<- [::selected-layer]
 :<- [::breakpoint.sub/active-breakpoints]
 (fn [[layer active-breakpoints] _]
   (or (breakpoint.util/get-prop layer active-breakpoints :z-position) 0)))

(rf/reg-sub
 ::selected-font
 :<- [::selected-layer]
 :<- [:story/font-db]
 :<- [::rich-text-editor/current-selection]
 :<- [::breakpoint.sub/active-breakpoints]
 :<- [::cta-sidebar.sub/interaction-state]
 (fn [[layer font-db current-selection active-breakpoints interaction-state] [_ {:keys [path]}]]
   (cond
     (= (:type layer) "rich-text")
     (let [{:keys [selection
                   content]}  current-selection
           anchor-path        (-> selection :anchor :path)
           focus-path         (-> selection :focus :path)
           selection-font-id  (when (and content anchor-path focus-path)
                                (let [[start-path stop-path] (sort [anchor-path focus-path])
                                      blocks                 (rich-text.content/block-nodes-between content start-path stop-path)]
                                  (when (= 1 (count blocks))
                                    (let [leaves (rich-text.content/leaf-nodes-between content start-path stop-path)]
                                      (some #(breakpoint.util/get-props % active-breakpoints :font-id) leaves)))))
           layer-bp-font-id   (breakpoint.util/get-props layer active-breakpoints :font-id)
           content-bp-font-id (when (vector? (:content layer))
                                (let [all-nodes (mapcat #(tree-seq map? :children %) (:content layer))
                                      map-nodes (filter map? all-nodes)]
                                  (some (fn [node]
                                          (let [result (breakpoint.util/get-props node active-breakpoints :font-id)]
                                            result))
                                        map-nodes)))
           font-id            (or selection-font-id content-bp-font-id layer-bp-font-id)]
       (when-let [f (and font-id (get font-db font-id))]
         f))
     path
     (let [layer-font-id (breakpoint.util/get-path layer active-breakpoints path)]
       (when-let [f (and layer-font-id (get font-db layer-font-id))]
         f))
     :else
(let [layer-font-id (if (and (= (:type layer) "CTA")
                             (= interaction-state :hover))
                      (or (breakpoint.util/get-props layer active-breakpoints [:hover :font-id])
                          (breakpoint.util/get-props layer active-breakpoints :font-id))
                      (breakpoint.util/get-props layer active-breakpoints :font-id))]
  (when-let [f (and layer-font-id (get font-db layer-font-id))]
    f)))))

(rf/reg-sub
 ::content-text
 :<- [::selected-layer]
 (fn [{:keys [content]} _]
   (when content
     (layer.util/content-text content))))